/**
 * Document Generator Types
 * Frontend TypeScript types for the Document Generator module
 */

// =============================================================================
// Enums and Constants
// =============================================================================

export type DocumentCategory =
  | 'nda'
  | 'loa'
  | 'loc'
  | 'employment'
  | 'internship'
  | 'confidentiality'
  | 'compliance'
  | 'delegation'
  | 'policy'
  | 'other';

export type DocumentStatus =
  | 'draft'
  | 'pending_review'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'pending_signature'
  | 'signed'
  | 'expired'
  | 'archived';

export type PlaceholderType = 'text' | 'date' | 'select' | 'number' | 'textarea' | 'email';

export type ConfidentialityLevel = 'public' | 'internal' | 'confidential' | 'highly_confidential';

export type ApproverType = 'user' | 'role' | 'department';

export type AIImprovementMode =
  | 'professional'
  | 'simplified'
  | 'formal'
  | 'concise'
  | 'elaborate'
  | 'legal';

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  nda: 'Non-Disclosure Agreement',
  loa: 'Letter of Authorization',
  loc: 'Letter of Confirmation',
  employment: 'Employment Contract',
  internship: 'Internship Agreement',
  confidentiality: 'Confidentiality Agreement',
  compliance: 'Compliance Document',
  delegation: 'Delegation of Authority',
  policy: 'Policy Document',
  other: 'Other'
};

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  pending_signature: 'Pending Signature',
  signed: 'Signed',
  expired: 'Expired',
  archived: 'Archived'
};

export const CONFIDENTIALITY_LABELS: Record<ConfidentialityLevel, string> = {
  public: 'Public',
  internal: 'Internal',
  confidential: 'Confidential',
  highly_confidential: 'Highly Confidential'
};

// =============================================================================
// Template Types
// =============================================================================

export interface Placeholder {
  key: string;
  label: string;
  type: PlaceholderType;
  required: boolean;
  defaultValue?: string;
  options?: string[];
  validation?: {
    pattern?: string;
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
  };
  helpText?: string;
}

export interface LockedClause {
  id: string;
  content: string;
  reason: string;
  lockedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  lockedAt: string;
}

export interface JurisdictionVariant {
  jurisdictionCode: string;
  name: string;
  content: string;
  specificClauses?: {
    clauseId: string;
    content: string;
  }[];
}

export interface DocumentTemplate {
  _id: string;
  name: string;
  slug: string;
  category: DocumentCategory;
  description: string;
  content: string;
  placeholders: Placeholder[];
  lockedClauses: LockedClause[];
  jurisdictionVariants: JurisdictionVariant[];
  defaultJurisdiction: string;
  version: string;
  isActive: boolean;
  isArchived: boolean;
  requiresApproval: boolean;
  allowedRoles: string[];
  allowedDepartments: string[];
  tags: string[];
  usageCount: number;
  lastUsed?: string;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  updatedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Generated Document Types
// =============================================================================

export interface DocumentVersion {
  versionNumber: number;
  content: string;
  createdAt: string;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  changeDescription: string;
}

export interface ApprovalStep {
  stepNumber: number;
  name: string;
  status: 'pending' | 'approved' | 'rejected' | 'skipped';
  approverType: ApproverType;
  approverRole?: string;
  approverUserId?: string;
  approverDepartment?: string;
  actionBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  actionAt?: string;
  comments?: string;
  isOptional: boolean;
}

export interface Signature {
  signedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  signedAt: string;
  signatureType: 'electronic' | 'digital' | 'manual';
  verified: boolean;
}

export interface Attachment {
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  uploadedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  uploadedAt: string;
}

export interface GeneratedDocument {
  _id: string;
  title: string;
  documentNumber: string;
  template: {
    _id: string;
    name: string;
    slug: string;
    category: DocumentCategory;
  };
  templateVersion: string;
  category: DocumentCategory;
  content: string;
  filledValues: Record<string, any>;
  versions: DocumentVersion[];
  status: DocumentStatus;
  approvalSteps: ApprovalStep[];
  currentApprovalStep: number;
  signatures: Signature[];
  relatedEmployeeId?: string;
  relatedDepartment?: string;
  relatedContract?: {
    _id: string;
    contractTitle: string;
    contractNumber: string;
  };
  effectiveDate?: string;
  expirationDate?: string;
  generatedPdfUrl?: string;
  generatedDocxUrl?: string;
  attachments: Attachment[];
  jurisdiction?: string;
  confidentialityLevel: ConfidentialityLevel;
  tags: string[];
  notes?: string;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  lastModifiedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  approvedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  approvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Approval Workflow Types
// =============================================================================

export interface WorkflowStep {
  stepNumber: number;
  name: string;
  approverType: ApproverType;
  approverRole?: string;
  approverUserId?: string;
  approverDepartment?: string;
  isOptional: boolean;
  canSkip: boolean;
  escalationDays?: number;
  escalateTo?: string;
  instructions?: string;
}

export interface ApprovalWorkflow {
  _id: string;
  name: string;
  description: string;
  steps: WorkflowStep[];
  applicableCategories: DocumentCategory[];
  applicableDepartments: string[];
  isDefault: boolean;
  isActive: boolean;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  updatedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PendingApproval {
  document: GeneratedDocument;
  step: ApprovalStep;
  canApprove: boolean;
  reason?: string;
}

// =============================================================================
// API Request/Response Types
// =============================================================================

export interface CreateTemplateInput {
  name: string;
  category: DocumentCategory;
  content: string;
  description?: string;
  placeholders?: Placeholder[];
  requiresApproval?: boolean;
  allowedRoles?: string[];
  allowedDepartments?: string[];
  tags?: string[];
  defaultJurisdiction?: string;
}

export interface UpdateTemplateInput {
  name?: string;
  category?: DocumentCategory;
  content?: string;
  description?: string;
  placeholders?: Placeholder[];
  isActive?: boolean;
  requiresApproval?: boolean;
  allowedRoles?: string[];
  allowedDepartments?: string[];
  tags?: string[];
  defaultJurisdiction?: string;
}

export interface GenerateDocumentInput {
  templateId: string;
  title: string;
  filledValues: Record<string, any>;
  jurisdiction?: string;
  effectiveDate?: string;
  expirationDate?: string;
  relatedEmployeeId?: string;
  relatedDepartment?: string;
  relatedContractId?: string;
  confidentialityLevel?: ConfidentialityLevel;
  tags?: string[];
  notes?: string;
}

export interface UpdateDocumentInput {
  content?: string;
  changeDescription?: string;
  title?: string;
  notes?: string;
  tags?: string[];
  confidentialityLevel?: ConfidentialityLevel;
}

export interface CreateWorkflowInput {
  name: string;
  description?: string;
  steps: Array<{
    name: string;
    approverType: ApproverType;
    approverRole?: string;
    approverUserId?: string;
    approverDepartment?: string;
    isOptional?: boolean;
    canSkip?: boolean;
    escalationDays?: number;
    escalateTo?: string;
    instructions?: string;
  }>;
  applicableCategories?: DocumentCategory[];
  applicableDepartments?: string[];
  isDefault?: boolean;
}

export interface ApprovalActionInput {
  action: 'approve' | 'reject' | 'skip';
  comments?: string;
}

export interface LockClauseInput {
  clauseId: string;
  content: string;
  reason: string;
}

// =============================================================================
// Filter Types
// =============================================================================

export interface TemplateFilters {
  category?: DocumentCategory;
  search?: string;
  isActive?: boolean;
  tags?: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface DocumentFilters {
  status?: DocumentStatus | DocumentStatus[];
  category?: DocumentCategory;
  search?: string;
  createdBy?: string;
  templateId?: string;
  fromDate?: string;
  toDate?: string;
  relatedContractId?: string;
  jurisdiction?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface WorkflowFilters {
  search?: string;
  isActive?: boolean;
  category?: DocumentCategory;
  department?: string;
  page?: number;
  limit?: number;
}

// =============================================================================
// AI Types
// =============================================================================

export interface AIImprovementResult {
  original: string;
  improved: string;
  mode: AIImprovementMode;
  changes: string[];
  confidence: number;
}

export interface AlternativeSuggestion {
  text: string;
  style: string;
  reason: string;
}

export interface ComplianceCheckResult {
  isCompliant: boolean;
  issues: Array<{
    severity: 'high' | 'medium' | 'low';
    category: string;
    description: string;
    suggestion: string;
    location?: string;
  }>;
  recommendations: string[];
  overallScore: number;
}

export interface ClauseGenerationResult {
  clause: string;
  explanation: string;
  alternatives: string[];
  warnings: string[];
}

// =============================================================================
// Pagination Types
// =============================================================================

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: PaginationInfo;
  message?: string;
}

// =============================================================================
// Jurisdiction Types
// =============================================================================

export interface Jurisdiction {
  code: string;
  name: string;
}

export const SUPPORTED_JURISDICTIONS: Jurisdiction[] = [
  { code: 'US-DE', name: 'Delaware, USA' },
  { code: 'US-CA', name: 'California, USA' },
  { code: 'US-NY', name: 'New York, USA' },
  { code: 'US-TX', name: 'Texas, USA' },
  { code: 'UK', name: 'United Kingdom' },
  { code: 'EU', name: 'European Union' },
  { code: 'SG', name: 'Singapore' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'AU', name: 'Australia' },
  { code: 'JP', name: 'Japan' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'CA', name: 'Canada' }
];
